import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createCheckout, getUserProfile } from '../services/api.shop';
import { useFrontendAuth } from '../hooks/useAuth';
import { useTranslation } from '../context/TranslationContext';
import { useProtectedQuery } from '../hooks/useProtectedQuery';
import { afterCheckoutReserve } from '../utils/invalidateProductReservationCaches';
import { apiErrorMessage } from '../utils/apiErrorMessage';
import { translateWithFallback } from '../utils/translateWithFallback';
import AnimatedPetitoLogo from '../components/AnimatedPetitoLogo';

function checkoutReturnPath(productId: string | undefined, search: string): string {
    const base = productId ? `/checkout/${productId}` : '/home';
    return search ? `${base}?${search}` : base;
}

function productPath(productId: string | undefined): string {
    if (!productId) return '/home';
    const id = parseInt(productId, 10);
    return Number.isNaN(id) ? '/home' : `/products/${id}`;
}

/**
 * Shared buy handoff: branded secure-checkout transition, then Shopify invoice.
 * Optional ?childId= for silent My Journey link (no pick-a-baby UI).
 *
 * Back-button / bfcache:
 * - Leave for Shopify with location.replace so history skips this loading screen.
 * - If the browser still restores this page from bfcache, pageshow navigates away.
 */
const Checkout = () => {
    const { productId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user, isAuthReady } = useFrontendAuth();
    const { t } = useTranslation();
    const tr = (key: string, fb: string) => translateWithFallback(t, key, fb);
    const tRef = useRef(t);
    tRef.current = t;
    const [error, setError] = useState<string | null>(null);
    /** Prevents double createCheckout in React Strict Mode / re-renders. */
    const startedRef = useRef(false);
    const cancelledRef = useRef(false);

    const { data: profile } = useProtectedQuery({
        queryKey: ['userProfile'],
        queryFn: getUserProfile,
    });

    const search = searchParams.toString();
    const backToProduct = () => {
        cancelledRef.current = true;
        navigate(productPath(productId), { replace: true });
    };

    // Browser Back from Shopify often restores this page from bfcache mid-loading.
    // Leave immediately so the user is not stuck on "Redirecting…".
    useEffect(() => {
        const onPageShow = (e: PageTransitionEvent) => {
            if (!e.persisted) return;
            cancelledRef.current = true;
            navigate(productPath(productId), { replace: true });
        };
        window.addEventListener('pageshow', onPageShow);
        return () => window.removeEventListener('pageshow', onPageShow);
    }, [navigate, productId]);

    useEffect(() => {
        if (!isAuthReady) return;
        if (!user) {
            const from = checkoutReturnPath(productId, search);
            navigate('/login', { replace: true, state: { from } });
            return;
        }
        if (startedRef.current || !productId) return;

        const id = parseInt(productId, 10);
        if (Number.isNaN(id)) {
            setError(tRef.current('checkout.error') || 'Invalid product');
            return;
        }

        const childParam = searchParams.get('childId');
        const childId =
            childParam && !Number.isNaN(parseInt(childParam, 10))
                ? parseInt(childParam, 10)
                : undefined;

        startedRef.current = true;
        cancelledRef.current = false;

        (async () => {
            try {
                const response = await createCheckout({
                    items: [{ productId: id, quantity: 1 }],
                    customerEmail: user.email || profile?.email,
                    ...(childId != null && childId > 0 ? { childId } : {}),
                });
                if (cancelledRef.current) return;

                if (response?.invoiceUrl) {
                    await afterCheckoutReserve(queryClient, id);
                    if (cancelledRef.current) return;
                    // replace: Back from Shopify returns to product/list, not this splash
                    window.location.replace(response.invoiceUrl);
                } else {
                    setError(tRef.current('checkout.error') || 'Checkout failed');
                    startedRef.current = false;
                }
            } catch (err) {
                if (cancelledRef.current) return;
                console.error('Checkout failed', err);
                setError(
                    apiErrorMessage(
                        err,
                        tRef.current('checkout.error') || 'Checkout failed. Please try again.'
                    )
                );
                startedRef.current = false;
            }
        })();
        // Do not depend on `t` - translation refresh must not re-start checkout.
    }, [isAuthReady, user, productId, navigate, profile?.email, searchParams, search, queryClient]);

    if (error) {
        return (
            <div className="mx-auto flex min-h-[50vh] max-w-md flex-col items-center justify-center px-4 py-10 text-center sm:py-16">
                <p className="mb-4 break-words text-red-600">{error}</p>
                <button
                    type="button"
                    onClick={backToProduct}
                    className="min-h-11 rounded-xl bg-[var(--accent-color)] px-6 py-2 text-sm font-semibold text-white"
                >
                    {tr('common.back', 'Back')}
                </button>
            </div>
        );
    }

    return (
        <div
            className="petito-splash max-lg:pb-mobile-nav"
            role="status"
            aria-live="polite"
            aria-busy="true"
        >
            <AnimatedPetitoLogo size="xl" playful mode="css" decorative />
            <p className="petito-splash__label">
                {tr('product.details.shopifyredirect', 'Redirecting to secure checkout...')}
            </p>
            <ul className="mt-1 max-w-sm list-none space-y-2 px-2 text-sm leading-relaxed text-[var(--text-color)]/75">
                <li>{tr('checkout.trust.secure', 'Secure payment via Shopify (Bancontact, card, PayPal).')}</li>
                <li>
                    {tr(
                        'checkout.trust.protection',
                        'Buyer protection is added at checkout as a separate line.'
                    )}
                </li>
                <li>
                    {tr(
                        'checkout.trust.help',
                        'After delivery, you can report a problem via your order or the helpdesk.'
                    )}
                </li>
            </ul>
            <button
                type="button"
                onClick={backToProduct}
                className="mt-6 min-h-11 rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_40%)] bg-white/50 px-5 py-2 text-sm font-medium text-[var(--text-color)] transition hover:bg-white/80"
            >
                {tr('checkout.cancel.back', 'Cancel and go back')}
            </button>
        </div>
    );
};

export default Checkout;
